import { MetricDrilldown as SharedMetricDrilldown } from "@/components/metrics/metric-drilldown";
import {
  type ChannelMetrics,
  useChannelMetricDrilldown,
  useChannelsByIds,
} from "@/hooks/api/use-channel-metrics";
import type { MetricsFilters } from "@/lib/filters";
import { METRIC_CONFIG } from "./channel-metrics-config";

// Mapping from derived metrics to their base metrics for drilldown
// Amount metrics map to their count equivalents to reuse the same drilldown
const METRIC_TO_BASE: Record<string, keyof ChannelMetrics> = {
  opportunities_influenced_amount: "opportunities_influenced",
  opportunities_influenced_won_amount: "opportunities_influenced_won",
  opportunities_influenced_in_cycle_amount: "opportunities_influenced_in_cycle",
  opportunities_created_post_engagement_amount:
    "opportunities_created_post_engagement",
  progressed_opportunities_amount: "progressed_opportunities",
  opportunities_activated_amount: "opportunities_activated",
  opportunities_activated_won_amount: "opportunities_activated_won",
  sourced_opportunities_amount: "sourced_opportunities",
  // Per-entity metrics map to their entity totals
  engagements_per_account: "accounts_engaged",
  engagements_per_person: "people_engaged",
  engagements_per_deal_contact: "deal_contacts_engaged",
};

interface MetricDrilldownProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channelType: string | null;
  metric: keyof typeof METRIC_CONFIG;
  metricValue?: number | string;
  statuses: string[];
  filters: MetricsFilters;
  metrics?: ChannelMetrics;
  drillDownLimit?: number;
  loadingStates?: Record<
    keyof ChannelMetrics,
    { isLoading: boolean; isError: boolean }
  >;
}

/**
 * Channels-specific wrapper for the shared MetricDrilldown component.
 * Handles channel-specific data fetching and passes data to the generic component.
 */
export function MetricDrilldown(props: MetricDrilldownProps) {
  const {
    open,
    onOpenChange,
    channelType,
    metric,
    metricValue: providedMetricValue,
    statuses,
    filters,
    metrics,
    loadingStates,
    drillDownLimit,
  } = props;

  // Map derived metrics (like amount metrics) to their base metrics for drilldown
  // This reuses the same drilldown table/columns for amount metrics as their count equivalents
  const drilldownMetric = (METRIC_TO_BASE[metric] ||
    metric) as keyof ChannelMetrics;

  // Use drilldownMetric's config for the actual drilldown data structure
  // Amount/per-entity metrics will use their base metric's config (e.g., opportunities_influenced_won)
  const drilldownConfig = METRIC_CONFIG[drilldownMetric];

  // Check if drilldown should be enabled based on metric value and limit
  // Use the provided metric value from the table row, or fall back to metrics query
  const metricValue = providedMetricValue ?? metrics?.[metric];
  const isMetricValueNumber = typeof metricValue === "number";
  // Skip limit check for amount/currency metrics since they show dollar values, not row counts
  const isAmountMetric = metric.toString().includes("amount");
  const isDrilldownLimitExceeded =
    drillDownLimit !== undefined &&
    isMetricValueNumber &&
    !isAmountMetric &&
    metricValue >= drillDownLimit;

  // Fetch drilldown data only if limit is not exceeded
  const { data: drilldownData, isLoading } = useChannelMetricDrilldown({
    channelType,
    metric: drilldownMetric, // Use the base metric for drilldown
    statuses,
    filters,
    enabled: open && !!channelType && !isDrilldownLimitExceeded,
  });

  const shouldShowLoading =
    isLoading || (open && !drilldownData && !isDrilldownLimitExceeded);

  // Fetch channel details to get the name for CSV export
  const { data: channelData } = useChannelsByIds(
    channelType ? [channelType] : undefined,
  );
  const channelName =
    channelData?.results?.[0]?.name || channelType || "channel";

  // Create metrics object with the provided value for limit checking
  // This ensures the limit check works immediately without waiting for API query
  const metricsWithProvidedValue =
    providedMetricValue !== undefined
      ? { ...metrics, [metric]: providedMetricValue }
      : metrics;

  // Override loading state for the metric if we have a provided value
  // This ensures the metric value displays immediately in the dialog header
  const loadingStatesWithProvidedValue =
    providedMetricValue !== undefined && loadingStates
      ? { ...loadingStates, [metric]: { isLoading: false, isError: false } }
      : loadingStates;

  return (
    <SharedMetricDrilldown
      open={open}
      onOpenChange={onOpenChange}
      metric={metric as string}
      metricConfig={drilldownConfig as never} // Use drilldown config for table structure
      metrics={metricsWithProvidedValue as Record<string, unknown>}
      drilldownData={drilldownData}
      isLoadingDrilldown={shouldShowLoading}
      loadingStates={loadingStatesWithProvidedValue}
      drillDownLimit={drillDownLimit}
      entityName={channelName}
      tableName={`channels::drilldown::${drilldownMetric}`} // Use base metric for table name
    />
  );
}
